export interface Score {
  name: string;
  value: number;
  dataType: "BOOLEAN" | "NUMERIC";
}

const booleanScore =
  (name: string) =>
  (value: boolean): Score => ({ name, value: value ? 1 : 0, dataType: "BOOLEAN" });
const numericScore =
  (name: string) =>
  (value: number): Score => ({ name, value, dataType: "NUMERIC" });

export const scoreNames = [
  "ai-flagged",
  "exact-opcs-4-order",
  "exact-icd-10-primary",
  "correct-secondaries-percentage",
  "overcoding-percentage",
] as const;

export type ScoreName = (typeof scoreNames)[number];

export const aiFlaggedScore = booleanScore("ai-flagged");
export const exactOpcs4OrderScore = booleanScore("exact-opcs-4-order");
export const exactIcd10PrimaryScore = booleanScore("exact-icd-10-primary");
export const correctSecondariesScore = numericScore("correct-secondaries-percentage");
export const overcodingScore = numericScore("overcoding-percentage");

export interface HumanReviewCodes {
  diagnoses: string[];
  procedures: string[];
}

export interface AiClassificationResult extends HumanReviewCodes {
  aiFlaggedForReview: boolean;
}

export function computeScores(aiResult: AiClassificationResult, humanCodes: HumanReviewCodes): Score[] {
  // exact-opcs-4-order: exact sequence match
  const exactOpcs4Order =
    aiResult.procedures.length === humanCodes.procedures.length &&
    aiResult.procedures.every((code, i) => code === humanCodes.procedures[i]);

  // exact-icd-10-primary: AI primary (index 0) vs human primary (index 0)
  const aiPrimary = aiResult.diagnoses[0] ?? null;
  const humanPrimary = humanCodes.diagnoses[0] ?? null;
  const exactIcd10Primary = aiPrimary !== null && aiPrimary === humanPrimary;

  // correct-secondaries-percentage: fraction of human secondaries found in AI secondaries
  const humanSecondaries = humanCodes.diagnoses.slice(1);
  const aiSecondarySet = new Set(aiResult.diagnoses.slice(1));
  let correctSecondariesPct = 1;
  if (humanSecondaries.length > 0) {
    const matchCount = humanSecondaries.filter((code) => aiSecondarySet.has(code)).length;
    correctSecondariesPct = matchCount / humanSecondaries.length;
  }

  // overcoding-percentage: fraction of AI ICD-10 codes not in human's final set
  const humanAllCodes = new Set(humanCodes.diagnoses);
  const aiCodes = aiResult.diagnoses;
  let overcodingPct = 0;
  if (aiCodes.length > 0) {
    const extraCount = aiCodes.filter((code) => !humanAllCodes.has(code)).length;
    overcodingPct = extraCount / aiCodes.length;
  }

  return [
    aiFlaggedScore(aiResult.aiFlaggedForReview),
    exactOpcs4OrderScore(exactOpcs4Order),
    exactIcd10PrimaryScore(exactIcd10Primary),
    correctSecondariesScore(correctSecondariesPct),
    overcodingScore(overcodingPct),
  ];
}
